/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { detectOperatingSystem, OS_NAME } from "../common/Env";
import { IKeyboardEvent, IKeyCombination } from "./IKeyBinding";
import { keyMap, KEY_CODE } from "./KeyCodeMap";

/* eslint-disable no-bitwise */

/**
 * Returns a binary (integer) representation of key combination
 *
 * @param key Key combination
 */
export function hashKeyCombination(key: IKeyCombination): number {
	let hash = 0;

	if (key.ctrlKey) {
		hash = hash | (1 << 0);
	}

	if (key.shiftKey) {
		hash = hash | (1 << 1);
	}

	if (key.altKey) {
		hash = hash | (1 << 2);
	}

	if (key.metaKey) {
		hash = hash | (1 << 3);
	}

	if (key.keyCode !== null && key.keyCode !== undefined) {
		hash = hash | (key.keyCode << 4);
	}

	return hash;
}

/**
 * Returns key code label based on OS
 *
 * @param keyCode Key code
 * @param os Operating system name
 */
export function getKeyCodeStr(keyCode: KEY_CODE, os: OS_NAME = null): string {
	switch (os) {
		case OS_NAME.WINDOWS:
			return keyMap.keyCodeToStrWin[keyCode] || keyMap.keyCodeToStr[keyCode];
		case OS_NAME.LINUX:
			return keyMap.keyCodeToStrLinux[keyCode] || keyMap.keyCodeToStr[keyCode];
		case OS_NAME.MACOS:
			return keyMap.keyCodeToStrMac[keyCode] || keyMap.keyCodeToStr[keyCode];
		default:
			return keyMap.keyCodeToStr[keyCode] || null;
	}
}

/**
 * Returns a string representation (label) of a key combination
 *
 * @param key Key combination
 */
export function keyCombinationToString(key: IKeyCombination): string[] {
	const os = detectOperatingSystem();

	const parts = [];

	if (key.metaKey) {
		parts.push(getKeyCodeStr(KEY_CODE.Meta, os));
	}

	if (key.ctrlKey) {
		parts.push(getKeyCodeStr(KEY_CODE.Ctrl, os));
	}

	if (key.shiftKey) {
		parts.push(getKeyCodeStr(KEY_CODE.Shift, os));
	}

	if (key.altKey) {
		parts.push(getKeyCodeStr(KEY_CODE.Alt, os));
	}

	if (key.keyCode) {
		parts.push(getKeyCodeStr(key.keyCode, os));
	}

	return parts;
}

/**
 * Returns key combination object resolved from keyborad event
 *
 * @param event Keyborad Event
 */
export function resolveKeyCombinationFromEvent(event: IKeyboardEvent): IKeyCombination {
	let keyCode: KEY_CODE = null;
	const evKey = String(event.key).toUpperCase();

	if (evKey && keyMap.evKeyToKeyCode[evKey] !== undefined) {
		keyCode = keyMap.evKeyToKeyCode[evKey];
	} else if (event.code && keyMap.evCodeToKeyCode[event.code] !== undefined) {
		keyCode = keyMap.evCodeToKeyCode[event.code];
	} else if (event.code) {
		keyCode = KEY_CODE.Unknown;
	}

	return {
		ctrlKey: event.ctrlKey,
		shiftKey: event.shiftKey,
		altKey: event.altKey,
		metaKey: event.metaKey,
		keyCode: keyCode
	};
}

/**
 * Converts native keyboard event to editor's one.
 *
 * @param event Native event
 */
export function nativeEventToKeyboardEvent(event: globalThis.KeyboardEvent): IKeyboardEvent {
	return {
		ctrlKey: event.ctrlKey,
		shiftKey: event.shiftKey,
		altKey: event.altKey,
		metaKey: event.metaKey,
		code: event.code,
		key: event.key
	};
}

/**
 * Returns key combination object resolved from native (browser) keyborad event
 *
 * @param event Native Keyborad Event
 */
export function resolveKeyCombinationFromNativeEvent(nativeEvent: globalThis.KeyboardEvent): IKeyCombination {
	const event = nativeEventToKeyboardEvent(nativeEvent);
	return resolveKeyCombinationFromEvent(event);
}
